// LocalCandidateDetailPage.js
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { useTranslation } from 'react-i18next';

import useLocalCandidate from '../../hooks/useLocalCandidate';
import PartyBadge from '../elections/parties/PartyBadge';

import './MemberDetailPage.css';
import './CandidateDetailPage.css';

/**
 * LocalCandidateDetailPage
 *
 * Profile page for one state, county or city candidate, mounted at
 * /us/candidate/local/:stateAbbr/:level/:ref.
 *
 * A separate page from CandidateDetailPage rather than a mode of it. That page
 * is keyed on the FEC candidate ID from the first line to the last: useCandidate
 * parses office and state out of it, CandidateFinancials is queried with it, and
 * the collection item is identified by it. A county commissioner candidate has
 * no FEC ID, so none of those three has anything to work with.
 *
 * The one principle that does carry over is the important one: everything
 * rendered here derives from the URL, so a pasted or shared link behaves exactly
 * like a click-through. See utils/localCandidateRef for how an address is
 * composed for a row that has no ID.
 *
 * Narrower again than the federal page, which is the whole page below the
 * profile: MemberDetailPage-style tabs need votes and sponsored bills, and
 * CandidateDetailPage's finance panel needs an FEC filing. A county or state
 * candidate has none of the three.
 *
 * No campaign finance section at all, not even a note explaining its absence.
 * A heading over a sentence saying the heading does not apply is still a
 * heading, and it took as much vertical space as real content would have. What
 * a reader can actually use -- the filing itself and the archived copy of it --
 * is on the page above.
 *
 * The party is a link to its party page, scoped to this candidate's state, so
 * a reader can go from one county candidate to what their party stands for
 * and who else it is running. The label stays as the state printed it; see
 * PartyBadge for how, and when, it resolves to a party.
 */

const openCapacitorSite = async (link) => {
  try {
    await Browser.open({ url: link });
  } catch (err) {
    console.error('Failed to open link:', err);
  }
};

function LocalCandidateDetailPage() {
  const { t } = useTranslation('common');
  const { stateAbbr, level, ref } = useParams();

  const normalizedState = String(stateAbbr || '').toUpperCase();

  const { candidate, loading, error } = useLocalCandidate(
    normalizedState,
    String(level || '').toLowerCase(),
    ref
  );

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderProfile = () => {
    if (loading) {
      return (
        <div className="member-profile-container">
          <Spinner id="spinner" animation="grow" variant="light" />
        </div>
      );
    }

    if (error || !candidate) {
      // Two different failures, and they mean opposite things. A row that is
      // genuinely absent is the data speaking; a request that failed is us. The
      // first wording sent a reader away from a candidate who was still there,
      // because the lookup had been rejected rather than come back empty.
      const missing = error?.notFound || (!error && !candidate);

      return (
        <div className="member-profile-container">
          <p className="member-detail-text">
            {missing
              ? // A local filing can genuinely stop being published between one
                // visit and the next -- the state reposts its file and a
                // withdrawn candidate is simply gone.
                t(
                  'common:localCandidateNotFound',
                  'This candidate is no longer in the state\u2019s published filings.'
                )
              : t(
                  'common:localCandidateLoadError',
                  'This candidate could not be loaded right now. Try again shortly.'
                )}
          </p>
        </div>
      );
    }

    // Built by filtering rather than with fixed separators, so a missing
    // district or party never leaves a dangling dot. The party is appended
    // separately because it renders as a control rather than as text.
    const subtitleParts = [candidate.office, candidate.county || candidate.jurisdiction].filter(
      Boolean
    );

    return (
      <div className={`candidate-profile ${isDesktop ? 'candidate-profile--desktop' : ''}`}>
        <div className="member-name">
          <span>{candidate.displayName}</span>
        </div>

        {(subtitleParts.length > 0 || candidate.party) && (
          <div className="member-subtitle">
            <span>{subtitleParts.join(' · ')}</span>
            {subtitleParts.length > 0 && candidate.party && <span> · </span>}
            {candidate.party && (
              <PartyBadge
                label={candidate.party}
                partyKey={candidate.partyKey || candidate.party_key}
                stateAbbr={normalizedState}
              />
            )}
          </div>
        )}

        {candidate.district && (
          <div className="member-subtitle">
            {t('common:districtLabel', 'District {{district}}', {
              district: candidate.district,
            })}
          </div>
        )}

        {candidate.year && (
          <div className="member-subtitle">
            {/* The cycle year, not a date. These rows carry no election date --
                only the year and, where a state records it, the day the
                candidate filed. */}
            {t('common:electionYear', 'Election: {{year}}', { year: candidate.year })}
          </div>
        )}

        {candidate.filedDate && (
          <div className="member-subtitle">
            {t('common:filedDate', 'Filed {{date}}', { date: candidate.filedDate })}
          </div>
        )}

        {/* Scoped class alongside the shared one. The federal page only ever
            renders a single FEC link here, so .member-contact never needed to
            separate siblings -- these rows can carry four, and without a gap
            they run into each other as one word. */}
        <div className="member-contact local-candidate-links">
          {candidate.phone && (
            <a className="member-link" href={`tel:${candidate.phone}`}>
              {candidate.phone}
            </a>
          )}

          {candidate.email && candidate.email.includes('@') && (
            <a className="member-link" href={`mailto:${candidate.email}`}>
              {candidate.email}
            </a>
          )}

          {candidate.campaignWebsite && (
            <span
              className="member-link"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                openCapacitorSite(candidate.campaignWebsite);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCapacitorSite(candidate.campaignWebsite);
                }
              }}
            >
              {t('common:campaignWebsite', 'Campaign website')}
            </span>
          )}

          {/* Every local row carries the government page it was read from.
              About one in a thousand is an artifact rather than a person -- a
              name field that picked up "Send Email" from a link, or a truncated
              surname -- and this is how a reader checks one for themselves. It
              is the closest thing this page has to the federal page's FEC
              record link, and it does the same job. */}
          {/* The archived copy, offered alongside the live one rather than
              instead of it. A county PDF is routinely moved or deleted within a
              year of the election, and this is the copy the API kept. */}
          {candidate.archivedSourceUrl && (
            <span
              className="member-link"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                openCapacitorSite(candidate.archivedSourceUrl);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCapacitorSite(candidate.archivedSourceUrl);
                }
              }}
            >
              {t('common:archivedFiling', 'Archived copy')}
            </span>
          )}

          {candidate.sourceUrl && (
            <span
              className="member-link"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                openCapacitorSite(candidate.sourceUrl);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCapacitorSite(candidate.sourceUrl);
                }
              }}
            >
              {t('common:filingSource', 'Filing source')}
            </span>
          )}
        </div>

        {candidate.contactSources.length > 0 && (
          <div className="member-subtitle local-candidate-provenance">
            {t('common:publishedBy', 'Published by {{source}}', {
              source: candidate.contactSources[0].published || '',
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="feedDisplay" className="member-detail-page">
      <div className="member-page-content">
        {renderProfile()}

      </div>

      <style>{`
        /* Match the Glance column width on the homepage so cards read as cards
           rather than full-width bars on wide desktop screens. */
        .member-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }

        /* Wraps rather than scrolls: "Archived copy" and "Filing source" sit
           side by side on a desktop column and stack on a phone. Centered to
           line up with the name and subtitles above them, which are centered by
           .candidate-profile. */
        .local-candidate-links {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.35rem 1.25rem;
          margin-top: 0.75rem;
        }
      `}</style>
    </div>
  );
}

export default LocalCandidateDetailPage;